import type { MetacategoryData } from '@openx/types/targeting/metacategory.ts';
import type { TargetingFieldsValues, TargetingReadOnlyProps } from '@openx/types/targeting/targeting.ts';
import { Criteria } from '@openx/types/targeting/targetingValuesTypes.ts';
import { useTranslation } from 'react-i18next';
import { useTargetingDisabledState } from '../../../utils/hooks/useTargetingDisabledState.ts';
import { useDropdownOpen } from '../../shared/ReadOnlyTargetingDropdown/hooks/useDropdownOpen.ts';
import { StyledReadOnlyTargetingDropdown } from '../../shared/ReadOnlyTargetingDropdown/ReadOnlyTargetingDropdown.ts';
import { TargetingReadOnly } from '../../shared/TargetingReadOnly/TargetingReadOnly.tsx';
import { MetacategoryFormPreviewItems } from './MetacategoryItems/MetacategoryFormPreviewItems.tsx';
import { MetacategoryItems } from './MetacategoryItems/MetacategoryItems.tsx';

type MetacategoryReadOnlyProps = TargetingReadOnlyProps<Partial<MetacategoryData>> & {
  field: TargetingFieldsValues;
  onRemoveCategories?: () => void;
  onRemoveKeywords?: () => void;
};

export const MetacategoryReadOnly = ({
  name,
  field,
  onOpenDrawerClick,
  onRemoveClick,
  targetingParams,
  readonly,
  onRemoveCategories,
  onRemoveKeywords,
}: MetacategoryReadOnlyProps) => {
  const { isDisabledOrHasError, disabledReasonMessage } = useTargetingDisabledState(field);

  const { t } = useTranslation();
  const { exclude_mfa, excludes, includes, keywords, inter_dimension_operator } = targetingParams || {};

  const isMetacategoryEmpty =
    !excludes?.length && !includes?.val?.length && !keywords?.includes?.length && !keywords?.excludes?.length;

  const hasMetacategories = Boolean(
    (targetingParams?.includes?.val && targetingParams.includes.val.length > 0) ||
      (targetingParams?.excludes && targetingParams.excludes.length > 0)
  );

  const hasKeywords = Boolean(
    (targetingParams?.keywords?.excludes && targetingParams.keywords.excludes.length > 0) ||
      (targetingParams?.keywords?.includes && targetingParams.keywords.includes.length > 0)
  );

  const optionsCount = [hasMetacategories, hasKeywords].filter(Boolean).length;
  const hasValue = optionsCount > 0;
  const { isOpen, toggleOpen } = useDropdownOpen(hasValue);

  const normalizedTargetingParams = {
    exclude_mfa: exclude_mfa || false,
    excludes: excludes || [],
    includes: includes || { op: Criteria.ANY, val: [] },
    inter_dimension_operator: inter_dimension_operator || Criteria.ANY,
    keywords: {
      excludes: keywords?.excludes || [],
      includes: keywords?.includes || [],
    },
  };

  if (!readonly)
    return (
      <TargetingReadOnly
        name={name}
        field={field}
        isEmpty={isMetacategoryEmpty}
        onOpenDrawerClick={onOpenDrawerClick}
        onRemove={onRemoveClick}
        readonly={readonly}
        isDisabled={isDisabledOrHasError}
        description={t(
          'Access proprietary OpenX-defined categories and keyword-based targeting options that extend beyond standard industry classifications for enhanced precision.'
        )}
        optionsCount={optionsCount}
        isOpen={isOpen}
        toggleOpen={toggleOpen}
        disabledReasonMessage={disabledReasonMessage}
      >
        {isOpen && (
          <StyledReadOnlyTargetingDropdown>
            <MetacategoryFormPreviewItems
              targetingParams={normalizedTargetingParams}
              optionsCount={optionsCount}
              onOpenDrawerClick={onOpenDrawerClick}
              onRemove={onRemoveClick}
              readonly={readonly}
              onRemoveCategories={onRemoveCategories}
              onRemoveKeywords={onRemoveKeywords}
              disabledReasonMessage={disabledReasonMessage}
              field={field}
            />
          </StyledReadOnlyTargetingDropdown>
        )}
      </TargetingReadOnly>
    );

  return (
    <TargetingReadOnly
      name={name}
      field={field}
      isEmpty={isMetacategoryEmpty}
      onOpenDrawerClick={onOpenDrawerClick}
      onRemove={onRemoveClick}
      readonly={readonly}
      isDisabled={isDisabledOrHasError}
      description={t(
        'Access proprietary OpenX-defined categories and keyword-based targeting options that extend beyond standard industry classifications for enhanced precision.'
      )}
      disabledReasonMessage={disabledReasonMessage}
    >
      {!isMetacategoryEmpty && <MetacategoryItems targetingParams={normalizedTargetingParams} field={field} />}
    </TargetingReadOnly>
  );
};
